import { useState } from 'react';
import { useBirthInfo } from '../../../contexts/BirthInfoContext';

const CX = 130;
const CY = 130;
const OUTER_R = 118;
const INNER_R = 82;
const PLANET_R = 62;
const CENTER_R = 46;

const ZODIAC_DATA = [
  { name: '양자리', symbol: '♈', element: 'fire', color: '#ef4444', en: 'Aries' },
  { name: '황소자리', symbol: '♉', element: 'earth', color: '#10b981', en: 'Taurus' },
  { name: '쌍둥이자리', symbol: '♊', element: 'air', color: '#fbbf24', en: 'Gemini' },
  { name: '게자리', symbol: '♋', element: 'water', color: '#60a5fa', en: 'Cancer' },
  { name: '사자자리', symbol: '♌', element: 'fire', color: '#f97316', en: 'Leo' },
  { name: '처녀자리', symbol: '♍', element: 'earth', color: '#34d399', en: 'Virgo' },
  { name: '천칭자리', symbol: '♎', element: 'air', color: '#a78bfa', en: 'Libra' },
  { name: '전갈자리', symbol: '♏', element: 'water', color: '#8b5cf6', en: 'Scorpio' },
  { name: '사수자리', symbol: '♐', element: 'fire', color: '#fb923c', en: 'Sagittarius' },
  { name: '염소자리', symbol: '♑', element: 'earth', color: '#6366f1', en: 'Capricorn' },
  { name: '물병자리', symbol: '♒', element: 'air', color: '#38bdf8', en: 'Aquarius' },
  { name: '물고기자리', symbol: '♓', element: 'water', color: '#c084fc', en: 'Pisces' },
] as const;

const SIGN_TRAITS: Record<string, { keyword: string; planet: string; quality: string }> = {
  '양자리': { keyword: '도전적, 에너지 넘침', planet: '화성', quality: '개척자' },
  '황소자리': { keyword: '안정적, 끈기 있음', planet: '금성', quality: '안정자' },
  '쌍둥이자리': { keyword: '지적, 호기심 많음', planet: '수성', quality: '소통자' },
  '게자리': { keyword: '감성적, 보호 본능', planet: '달', quality: '양육자' },
  '사자자리': { keyword: '카리스마, 창의력', planet: '태양', quality: '리더' },
  '처녀자리': { keyword: '분석적, 세밀함', planet: '수성', quality: '분석자' },
  '천칭자리': { keyword: '균형, 사교적', planet: '금성', quality: '조화자' },
  '전갈자리': { keyword: '직관적, 변혁적', planet: '명왕성', quality: '탐구자' },
  '사수자리': { keyword: '자유롭고 철학적', planet: '목성', quality: '탐험가' },
  '염소자리': { keyword: '목표 지향, 책임감', planet: '토성', quality: '건설자' },
  '물병자리': { keyword: '독창적, 인도주의', planet: '천왕성', quality: '혁신가' },
  '물고기자리': { keyword: '공감 능력, 직관', planet: '해왕성', quality: '몽상가' },
};

function toRad(deg: number) {
  return (deg - 90) * Math.PI / 180;
}

function pt(cx: number, cy: number, r: number, deg: number) {
  return { x: cx + r * Math.cos(toRad(deg)), y: cy + r * Math.sin(toRad(deg)) };
}

function segPath(cx: number, cy: number, innerR: number, outerR: number, startDeg: number, endDeg: number): string {
  const os = pt(cx, cy, outerR, startDeg);
  const oe = pt(cx, cy, outerR, endDeg);
  const ie = pt(cx, cy, innerR, endDeg);
  const is_ = pt(cx, cy, innerR, startDeg);
  return `M ${os.x} ${os.y} A ${outerR} ${outerR} 0 0 1 ${oe.x} ${oe.y} L ${ie.x} ${ie.y} A ${innerR} ${innerR} 0 0 0 ${is_.x} ${is_.y} Z`;
}

function midPt(cx: number, cy: number, r: number, startDeg: number, endDeg: number) {
  return pt(cx, cy, r, (startDeg + endDeg) / 2);
}

function getSunSignIndex(month: number, day: number): number {
  if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return 0;
  if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return 1;
  if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return 2;
  if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return 3;
  if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return 4;
  if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return 5;
  if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return 6;
  if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) return 7;
  if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) return 8;
  if ((month === 12 && day >= 22) || (month === 1 && day <= 19)) return 9;
  if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) return 10;
  return 11;
}

function getMoonSignIndex(birthDate: string): number {
  const date = new Date(birthDate);
  const daysSince = Math.floor(date.getTime() / (1000 * 60 * 60 * 24));
  return ((daysSince % 27) * 12 / 27 | 0) % 12;
}

function getRisingSignIndex(birthDate: string, birthTime: string): number {
  const date = new Date(birthDate);
  const parts = (birthTime || '12:00').split(':');
  const h = parseInt(parts[0], 10) || 12;
  const month = date.getMonth();
  return (Math.floor(h / 2) + month * 2 + 12) % 12;
}

const PLANET_ICONS = ['☿', '♀', '♂', '♃', '♄'];
const PLANET_NAMES = ['수성', '금성', '화성', '목성', '토성'];
const PLANET_COLORS = ['#94a3b8', '#f9a8d4', '#f87171', '#fbbf24', '#a78bfa'];

function getPlanetSignIndex(idx: number, birthDate: string, sunIdx: number): number {
  const date = new Date(birthDate);
  const y = date.getFullYear() - 2000;
  const m = date.getMonth();
  const d = date.getDate();
  const yp = (y + 1200) % 1200;
  switch (idx) {
    case 0: return (sunIdx + ((yp + m + d) % 3) - 1 + 12) % 12;
    case 1: return (sunIdx + ((yp * 2 + m + d) % 5) - 2 + 12) % 12;
    case 2: return Math.abs(Math.floor((yp * 12 + m) / 2) % 12);
    case 3: return Math.abs(yp % 12);
    case 4: return Math.abs(Math.floor(yp / 2.46) % 12);
    default: return 0;
  }
}

interface ChartData {
  sunIdx: number;
  moonIdx: number;
  risingIdx: number;
  planetIdxs: number[];
}

function buildChart(birthDate: string, birthTime: string): ChartData {
  const date = new Date(birthDate);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const sunIdx = getSunSignIndex(month, day);
  const moonIdx = getMoonSignIndex(birthDate);
  const risingIdx = getRisingSignIndex(birthDate, birthTime);
  const planetIdxs = PLANET_ICONS.map((_, i) => getPlanetSignIndex(i, birthDate, sunIdx));
  return { sunIdx, moonIdx, risingIdx, planetIdxs };
}

const ELEMENT_COLORS: Record<string, string> = {
  fire: 'rgba(239,68,68,0.15)',
  earth: 'rgba(16,185,129,0.15)',
  air: 'rgba(251,191,36,0.15)',
  water: 'rgba(96,165,250,0.15)',
};

export default function BirthChartPanel() {
  const { birthInfo } = useBirthInfo();
  const [isExpanded, setIsExpanded] = useState(true);

  if (!birthInfo) {
    return (
      <div className="max-w-4xl mx-auto mb-6 px-4 py-5 rounded-2xl bg-white/3 border border-white/8 flex items-center gap-4">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-purple-500/10 border border-purple-500/20 flex-shrink-0">
          <i className="ri-donut-chart-line text-purple-400 text-xl"></i>
        </div>
        <div>
          <p className="text-white/70 text-sm font-semibold">탄생 차트 시각화</p>
          <p className="text-purple-400/50 text-xs">홈에서 생년월일을 입력하면 개인 탄생 차트가 표시됩니다</p>
        </div>
      </div>
    );
  }

  const chart = buildChart(birthInfo.birthDate, birthInfo.birthTime);
  const sunSign = ZODIAC_DATA[chart.sunIdx];
  const moonSign = ZODIAC_DATA[chart.moonIdx];
  const risingSign = ZODIAC_DATA[chart.risingIdx];

  return (
    <div className="max-w-4xl mx-auto mb-6">
      <div className="rounded-2xl bg-gradient-to-br from-primary-dark/90 via-mystic-dark/80 to-primary-dark/90 border border-primary-gold/25 overflow-hidden">
        {/* Header */}
        <button
          onClick={() => setIsExpanded((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-3.5 cursor-pointer hover:bg-white/3 transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-purple-500/15 border border-purple-500/25">
              <i className="ri-donut-chart-line text-purple-300 text-base"></i>
            </div>
            <div className="flex items-center gap-2.5">
              <span className="text-white font-bold text-sm">탄생 차트 (Birth Chart)</span>
              <div className="flex items-center gap-1.5">
                <span className="px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400 text-xs font-semibold">{sunSign.symbol} {sunSign.name}</span>
                <span className="px-2 py-0.5 rounded-full bg-slate-500/15 border border-slate-500/30 text-slate-300 text-xs font-semibold">🌙 {moonSign.name}</span>
                <span className="px-2 py-0.5 rounded-full bg-purple-500/15 border border-purple-500/30 text-purple-300 text-xs font-semibold">↑ {risingSign.name}</span>
              </div>
            </div>
          </div>
          <div className="w-6 h-6 flex items-center justify-center">
            <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-purple-400 text-base`}></i>
          </div>
        </button>

        {/* Content */}
        {isExpanded && (
          <div className="px-5 pb-5 border-t border-white/6">
            <div className="flex flex-col lg:flex-row gap-6 pt-5">
              {/* SVG Zodiac Wheel */}
              <div className="flex-shrink-0 flex justify-center">
                <svg viewBox="0 0 260 260" width="260" height="260" className="overflow-visible">
                  <defs>
                    <radialGradient id="bgGrad" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="rgba(139,92,246,0.12)" />
                      <stop offset="100%" stopColor="rgba(0,0,0,0)" />
                    </radialGradient>
                    <filter id="glowGold">
                      <feGaussianBlur stdDeviation="3" result="blur" />
                      <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                  </defs>

                  {/* Background */}
                  <circle cx={CX} cy={CY} r={OUTER_R + 5} fill="url(#bgGrad)" />

                  {/* 12 Segment ring */}
                  {ZODIAC_DATA.map((sign, i) => {
                    const startDeg = i * 30;
                    const endDeg = (i + 1) * 30;
                    const isSun = i === chart.sunIdx;
                    const isMoon = i === chart.moonIdx;
                    const isRising = i === chart.risingIdx;
                    const fillColor = isSun
                      ? 'rgba(251,191,36,0.55)'
                      : isMoon
                      ? 'rgba(148,163,184,0.45)'
                      : isRising
                      ? 'rgba(167,139,250,0.45)'
                      : ELEMENT_COLORS[sign.element];
                    const strokeColor = isSun
                      ? 'rgba(251,191,36,0.9)'
                      : isMoon
                      ? 'rgba(148,163,184,0.7)'
                      : isRising
                      ? 'rgba(167,139,250,0.7)'
                      : 'rgba(255,255,255,0.08)';
                    const strokeW = isSun || isMoon || isRising ? 1.5 : 0.8;

                    const mid = midPt(CX, CY, (INNER_R + OUTER_R) / 2, startDeg, endDeg);
                    return (
                      <g key={i}>
                        <path
                          d={segPath(CX, CY, INNER_R, OUTER_R, startDeg, endDeg)}
                          fill={fillColor}
                          stroke={strokeColor}
                          strokeWidth={strokeW}
                          filter={isSun ? 'url(#glowGold)' : undefined}
                        />
                        <text
                          x={mid.x}
                          y={mid.y}
                          textAnchor="middle"
                          dominantBaseline="central"
                          fontSize={isSun || isMoon || isRising ? '13' : '11'}
                          fill={isSun ? '#fbbf24' : isMoon ? '#94a3b8' : isRising ? '#a78bfa' : 'rgba(255,255,255,0.5)'}
                          fontWeight={isSun || isMoon || isRising ? 'bold' : 'normal'}
                        >
                          {sign.symbol}
                        </text>
                      </g>
                    );
                  })}

                  {/* Division lines */}
                  {Array.from({ length: 12 }).map((_, i) => {
                    const p1 = pt(CX, CY, INNER_R, i * 30);
                    const p2 = pt(CX, CY, OUTER_R, i * 30);
                    return <line key={i} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="rgba(255,255,255,0.1)" strokeWidth="0.8" />;
                  })}

                  {/* Inner circle border */}
                  <circle cx={CX} cy={CY} r={INNER_R} fill="rgba(10,5,30,0.6)" stroke="rgba(139,92,246,0.25)" strokeWidth="1" />

                  {/* Planet dots ring */}
                  {chart.planetIdxs.map((signIdx, pi) => {
                    const deg = signIdx * 30 + 15 + pi * 3;
                    const pPt = pt(CX, CY, PLANET_R - pi * 6, deg);
                    return (
                      <g key={pi}>
                        <circle cx={pPt.x} cy={pPt.y} r={7} fill={PLANET_COLORS[pi] + '30'} stroke={PLANET_COLORS[pi]} strokeWidth="1.2" />
                        <text x={pPt.x} y={pPt.y} textAnchor="middle" dominantBaseline="central" fontSize="8" fill={PLANET_COLORS[pi]} fontWeight="bold">
                          {PLANET_ICONS[pi]}
                        </text>
                      </g>
                    );
                  })}

                  {/* Center circle */}
                  <circle cx={CX} cy={CY} r={CENTER_R} fill="rgba(8,3,20,0.85)" stroke="rgba(251,191,36,0.4)" strokeWidth="1.5" />

                  {/* Center content */}
                  <text x={CX} y={CY - 14} textAnchor="middle" dominantBaseline="central" fontSize="18" fill="#fbbf24" fontWeight="bold">
                    {sunSign.symbol}
                  </text>
                  <text x={CX} y={CY + 2} textAnchor="middle" dominantBaseline="central" fontSize="8.5" fill="rgba(255,255,255,0.8)" fontWeight="bold">
                    {sunSign.name}
                  </text>
                  <text x={CX} y={CY + 14} textAnchor="middle" dominantBaseline="central" fontSize="7.5" fill="rgba(251,191,36,0.6)">
                    태양궁
                  </text>

                  {/* 3 marker labels outside */}
                  {[
                    { idx: chart.sunIdx, label: '☀', color: '#fbbf24', offset: 14 },
                    { idx: chart.moonIdx, label: '🌙', color: '#94a3b8', offset: 8 },
                    { idx: chart.risingIdx, label: '↑', color: '#a78bfa', offset: 8 },
                  ].map(({ idx, label, color, offset }) => {
                    const deg = idx * 30 + 15;
                    const ePt = pt(CX, CY, OUTER_R + offset, deg);
                    return (
                      <text key={label} x={ePt.x} y={ePt.y} textAnchor="middle" dominantBaseline="central" fontSize="11" fill={color}>
                        {label}
                      </text>
                    );
                  })}
                </svg>
              </div>

              {/* Right: 3 main signs + planets */}
              <div className="flex-1 space-y-4 min-w-0">
                {/* 3 Main Signs */}
                <div className="space-y-2">
                  {[
                    { label: '태양궁 (Sun)', sign: sunSign, desc: '외면적 자아 · 본질적 성격', badge: '☀️', accent: 'border-amber-500/40 bg-amber-500/8' },
                    { label: '달자리 (Moon)', sign: moonSign, desc: '내면적 감성 · 무의식', badge: '🌙', accent: 'border-slate-500/30 bg-slate-500/8' },
                    { label: '상승궁 (Rising)', sign: risingSign, desc: '첫인상 · 외적 표현 방식', badge: '⬆️', accent: 'border-purple-500/35 bg-purple-500/8' },
                  ].map(({ label, sign, desc, badge, accent }) => {
                    const traits = SIGN_TRAITS[sign.name];
                    return (
                      <div key={label} className={`flex items-start gap-3 p-3 rounded-xl border ${accent}`}>
                        <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-white/5">
                          <span className="text-base">{badge}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-purple-400/60 text-xs">{label}</span>
                            <span className="text-white font-bold text-sm">{sign.symbol} {sign.name}</span>
                          </div>
                          <p className="text-purple-300/50 text-xs mt-0.5">{desc}</p>
                          {traits && (
                            <p className="text-purple-200/70 text-xs mt-1">
                              <span className="text-primary-gold/70 font-semibold">{traits.keyword}</span>
                              <span className="text-purple-400/50 ml-2">· {traits.planet} 지배</span>
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Planet Positions */}
                <div className="rounded-xl bg-white/3 border border-white/8 p-3">
                  <p className="text-purple-400/60 text-xs font-semibold uppercase tracking-wide mb-2">행성 배치</p>
                  <div className="grid grid-cols-5 gap-1.5">
                    {PLANET_ICONS.map((icon, i) => {
                      const signIdx = chart.planetIdxs[i];
                      const sign = ZODIAC_DATA[signIdx];
                      return (
                        <div key={i} className="flex flex-col items-center gap-1 p-2 rounded-lg bg-white/3 text-center">
                          <span className="text-sm" style={{ color: PLANET_COLORS[i] }}>{icon}</span>
                          <span className="text-[9px] font-bold" style={{ color: PLANET_COLORS[i] }}>{PLANET_NAMES[i]}</span>
                          <span className="text-[9px] text-white/60">{sign.symbol}</span>
                          <span className="text-[8px] text-purple-400/50 leading-tight">{sign.name.replace('자리', '')}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Element balance */}
                <div className="rounded-xl bg-white/3 border border-white/8 p-3">
                  <p className="text-purple-400/60 text-xs font-semibold uppercase tracking-wide mb-2">원소 분포</p>
                  {(['fire', 'earth', 'air', 'water'] as const).map((el) => {
                    const signs = [chart.sunIdx, chart.moonIdx, chart.risingIdx, ...chart.planetIdxs];
                    const count = signs.filter((idx) => ZODIAC_DATA[idx].element === el).length;
                    const elKo = { fire: '불(火)', earth: '흙(地)', air: '공기(風)', water: '물(水)' }[el];
                    const elColor = { fire: '#ef4444', earth: '#10b981', air: '#fbbf24', water: '#60a5fa' }[el];
                    return (
                      <div key={el} className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs w-14 text-purple-300/60">{elKo}</span>
                        <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${(count / 8) * 100}%`, backgroundColor: elColor, opacity: 0.8 }}
                          />
                        </div>
                        <span className="text-xs font-bold" style={{ color: elColor }}>{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
